import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tab,
  Tabs,
  Button,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  History,
  Payment,
  Description,
  Settings,
  CalendarToday,
  Group,
  Euro,
  ExitToApp,
  CardGiftcard
} from '@mui/icons-material';
import { ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';
import BookingHistory from './BookingHistory';
import PaymentHistory from './PaymentHistory';
import ProfileSettings from './ProfileSettings';
import DocumentList from './DocumentList';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `user-tab-${index}`,
    'aria-controls': `user-tabpanel-${index}`,
  };
}

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingBookings: 0,
    totalBooked: 0,
    totalPaid: 0,
    totalSpent: 0,
    lastBookingDate: null as string | null,
    nextTripDate: null as string | null
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, [isAuthenticated, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Load user statistics - FIXED: Use correct port 5174 instead of 5000
      const response = await fetch('/api/user/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#F5F0E6',
      position: 'relative',
      '&::before': {
        content: '"👤 KUNDEN-BEREICH"',
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: '#910F0F',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(145, 15, 15, 0.3)'
      }
    }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{
          p: 3,
          mb: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" gutterBottom>
                Mein Konto
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Willkommen zurück, {user?.firstName} {user?.lastName}!
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<CardGiftcard />}
              onClick={() => navigate('/gutschein-kaufen')}
              sx={{
                backgroundColor: '#910F0F',
                '&:hover': {
                  backgroundColor: '#7a0d0d'
                },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Gutschein kaufen
            </Button>
          </Box>
        </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Gesamte Buchungen - schmal */}
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper elevation={2} sx={{
            p: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}>
            <Box display="flex" alignItems="center">
              <History color="primary" sx={{ mr: 1.5 }} />
              <Box>
                <Typography variant="h6">{stats.totalBookings}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Gesamte Buchungen
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        {/* Bevorstehende Fahrten - breiter */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper elevation={2} sx={{
            p: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}>
            <Box display="flex" alignItems="center">
              <CalendarToday color="success" sx={{ mr: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{stats.upcomingBookings}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Bevorstehende Fahrten
                </Typography>
                {stats.nextTripDate && (
                  <Typography variant="body1" color="success.main" sx={{ fontWeight: 600, ml: 1 }}>
                    | Nächste: {new Date(stats.nextTripDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
        {/* Gebucht/Bezahlt - breiter */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper elevation={2} sx={{
            p: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}>
            <Box display="flex" alignItems="center">
              <Euro color="warning" sx={{ mr: 1.5 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Gebucht:</Typography>
                  <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                    {stats.totalBooked.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Bezahlt:</Typography>
                  <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 600 }}>
                    {stats.totalPaid.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        {/* Kundentyp - schmal */}
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper elevation={2} sx={{
            p: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}>
            <Box display="flex" alignItems="center">
              <Group color="info" sx={{ mr: 1.5 }} />
              <Box>
                <Typography variant="h6">
                  {user?.customerType === 'Business' ? 'Geschäft' : 'Privat'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Kundentyp
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper elevation={3} sx={{ 
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          '& .MuiTabs-root': {
            paddingLeft: 2,
            paddingRight: 2
          },
          '& .MuiTab-root': {
            borderRadius: '8px 8px 0 0',
            marginRight: 1,
            '&.Mui-selected': {
              backgroundColor: 'rgba(145, 15, 15, 0.04)'
            }
          }
        }}>
          <Tabs value={value} onChange={handleChange} aria-label="user dashboard tabs">
            <Tab icon={<History />} label="Buchungen" {...a11yProps(0)} />
            <Tab icon={<Payment />} label="Zahlungen" {...a11yProps(1)} />
            <Tab icon={<Description />} label="Dokumente" {...a11yProps(2)} />
            <Tab icon={<Settings />} label="Einstellungen" {...a11yProps(3)} />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          <BookingHistory />
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          <PaymentHistory />
        </TabPanel>
        
        <TabPanel value={value} index={2}>
          <DocumentList />
        </TabPanel>
        
        <TabPanel value={value} index={3}>
          <ProfileSettings />
        </TabPanel>
      </Paper>
      </Container>
    </Box>
  );
};

export default UserDashboard;
